import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from './api';

function PoliticianProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [politician, setPolitician] = useState(null);
    const [healthDetails, setHealthDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const politicianData = await api.getPolitician(parseInt(id));
                if (politicianData) {
                    setPolitician(politicianData);

                    // Fetch health details
                    const healthResponse = await fetch(`http://localhost:5000/api/health/${id}`);
                    if (healthResponse.ok) {
                        const healthData = await healthResponse.json();
                        setHealthDetails(healthData);
                    }
                } else {
                    setError('Politician not found');
                }
            } catch (err) {
                setError('Failed to load politician data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id]);

    const getHealthColor = (score) => {
        if (score >= 80) return '#2ECC71';
        if (score >= 60) return '#F39C12';
        if (score >= 40) return '#E67E22';
        return '#E74C3C';
    };

    const getPartyColor = (party) => {
        switch (party) {
            case 'Democrat': return '#4A90E2';
            case 'Republican': return '#E74C3C';
            case 'Independent': return '#95A5A6';
            default: return '#7F8C8D';
        }
    };

    const getHealthGrade = (score) => {
        if (score >= 90) return 'A';
        if (score >= 80) return 'B';
        if (score >= 70) return 'C';
        if (score >= 60) return 'D';
        return 'F';
    };

    const getHealthStatus = (score) => {
        if (score >= 80) return 'Excellent Health';
        if (score >= 60) return 'Good Health';
        if (score >= 40) return 'Health Concerns';
        return 'Critical Health Issues';
    };

    const formatCondition = (condition) => {
        return condition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'low': return '#2ECC71';
            case 'medium': return '#F39C12';
            case 'high': return '#E74C3C';
            default: return '#95A5A6';
        }
    };

    if (loading) {
        return (
            <div className="page">
                <div className="loading">Loading politician profile...</div>
            </div>
        );
    }

    if (error || !politician) {
        return (
            <div className="page">
                <div className="error-state">
                    <h2>🚫 {error || 'Politician Not Found'}</h2>
                    <p>We couldn't find the politician you're looking for.</p>
                    <button onClick={() => navigate('/politicians')} className="branch-button">
                        View All Politicians
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="profile-container">
                {/* Header Section */}
                <div className="profile-header">
                    <img
                        src={politician.image}
                        alt={politician.name}
                        className="profile-image"
                    />

                    <div className="profile-basic-info">
                        <h1>{politician.name}</h1>

                        <div
                            className="profile-party"
                            style={{ backgroundColor: getPartyColor(politician.party) }}
                        >
                            {politician.party}
                        </div>

                        <div className="profile-details">
                            {politician.state && <p><strong>State:</strong> {politician.state}</p>}
                            {politician.district && <p><strong>District:</strong> {politician.district}</p>}
                            {politician.position && <p><strong>Position:</strong> {politician.position}</p>}
                            <p><strong>Age:</strong> {politician.age} years old</p>
                            <p><strong>Years in Office:</strong> {politician.yearsInOffice}</p>
                        </div>
                    </div>

                    {/* Health Score Section */}
                    <div className="health-score-section">
                        <h3>Health Assessment</h3>
                        <div
                            className="health-score-large"
                            style={{ backgroundColor: getHealthColor(politician.healthScore) }}
                        >
                            <span className="score-number">{politician.healthScore}</span>
                            <span className="score-grade">{getHealthGrade(politician.healthScore)}</span>
                        </div>
                        <div className="health-breakdown">
                            <div className="health-bar">
                                <div
                                    className="health-fill"
                                    style={{
                                        width: `${politician.healthScore}%`,
                                        backgroundColor: getHealthColor(politician.healthScore)
                                    }}
                                ></div>
                            </div>
                            <p className="health-status">{getHealthStatus(politician.healthScore)}</p>
                        </div>
                    </div>
                </div>

                {/* Health Content Sections */}
                <div className="profile-content">
                    {/* Medical Conditions */}
                    {healthDetails?.medicalConditions?.length > 0 && (
                        <section className="profile-section">
                            <h3>📋 Known Medical Conditions</h3>
                            <div className="conditions-list">
                                {healthDetails.medicalConditions.map((condition, index) => (
                                    <div key={index} className="condition-item">
                                        {formatCondition(condition)}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Recent Health Events */}
                    {healthDetails?.recentHealthEvents?.length > 0 && (
                        <section className="profile-section">
                            <h3>🏥 Recent Health Events</h3>
                            <div className="health-events">
                                {healthDetails.recentHealthEvents.map((event, index) => (
                                    <div key={index} className="health-event">
                                        <div className="event-header">
                                            <span className="event-date">{new Date(event.date).toLocaleDateString()}</span>
                                            <span
                                                className="severity-badge"
                                                style={{ backgroundColor: getSeverityColor(event.severity) }}
                                            >
                                                {event.severity.toUpperCase()}
                                            </span>
                                        </div>
                                        <h4>{event.condition}</h4>
                                        <p>{event.description}</p>
                                        <div className="recovery-status">
                                            <strong>Recovery Status:</strong>
                                            <span className={`recovery-${event.recovered}`}>
                                                {event.recovered === true ? '✅ Fully Recovered' :
                                                    event.recovered === 'partial' ? '🔄 Partially Recovered' :
                                                        event.recovered === 'ongoing' ? '⏳ Ongoing Treatment' :
                                                            '❌ Not Recovered'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Public Health Disclosures */}
                    {healthDetails?.publicDisclosures?.length > 0 && (
                        <section className="profile-section">
                            <h3>📢 Public Health Disclosures</h3>
                            <div className="disclosures-list">
                                {healthDetails.publicDisclosures.map((disclosure, index) => (
                                    <div key={index} className="disclosure-item">
                                        <div className="disclosure-header">
                                            <span className="disclosure-date">{new Date(disclosure.date).toLocaleDateString()}</span>
                                            <span className="disclosure-source">{disclosure.source}</span>
                                        </div>
                                        <p>{disclosure.condition}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Age-Related Risk Factors */}
                    {healthDetails?.ageRiskFactors?.length > 0 && healthDetails.ageRiskFactors[0] !== 'none' && (
                        <section className="profile-section">
                            <h3>⚠️ Age-Related Health Risks</h3>
                            <div className="risk-factors">
                                {healthDetails.ageRiskFactors.map((risk, index) => (
                                    <div key={index} className="risk-item">
                                        {formatCondition(risk)}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Fitness Level */}
                    {healthDetails?.fitnessLevel && (
                        <section className="profile-section">
                            <h3>💪 Physical Fitness Level</h3>
                            <div className="fitness-level">
                                <div className={`fitness-badge fitness-${healthDetails.fitnessLevel.replace('_', '-')}`}>
                                    {formatCondition(healthDetails.fitnessLevel)}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Health Disclaimer */}
                    <section className="profile-section disclaimer">
                        <h3>ℹ️ Health Information Disclaimer</h3>
                        <p>
                            All health information is based on publicly available disclosures, news reports,
                            and age-related health statistics. This assessment is for informational purposes
                            only and should not be considered medical advice. Health scores are calculated
                            using a proprietary algorithm considering age, known medical conditions, recent
                            health events, and public fitness assessments.
                        </p>
                        <p>
                            <strong>Last Updated:</strong> {healthDetails?.lastHealthUpdate ?
                                new Date(healthDetails.lastHealthUpdate).toLocaleDateString() : 'Unknown'}
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default PoliticianProfile;